import {useAccessToken} from "../../../services/auth"
import React, {useState, useEffect, useRef} from "react"
import {
	getEmailTemplatesList,
	EmailTemplate,
	publishEmailTemplateVersion,
	createEmailTemplateVersion,
	createEmailTemplate,
} from "../../../resources/emailTemplates"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {DataTableCard} from "../../../components/DataTable/DataTable"
import Button from "../../../components/Button/Button"
import useAsyncResultIdle from "../../../hooks/useAsyncResultIdle"
import {useNavigate} from "react-router-dom"
import TitleBar from "../../../components/TitleBar/TitleBar"
import SearchBox from "../../../components/SearchBox/SearchBox"
import {DataTableActionHeader} from "../../../components/DataTable/DataTable"
import {useQueryState} from "use-location-state"
import {CreateEmailTemplate} from "./CreateEmailTemplate"
import {EditEmailTemplate} from "./EditEmailTemplate"
import {EmailTemplatesDataTable} from "./EmailTemplatesDataTable"
import {DomainFilter} from "../../../components/DomainFilter/DomainFilter"

export default function EmailTemplates({refresh}: {refresh: () => void}) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const result = useAsyncResult(() => getEmailTemplatesList(accessToken), [accessToken, refresh])
	const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
	const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
	const [isCreating, setIsCreating] = useState(false)
	const [newTemplateId, setNewTemplateId] = useState("")
	const [newTemplateSubject, setNewTemplateSubject] = useState("")
	const [error, setError] = useState<string | null>(null)
	const [forcePublish, setForcePublish] = useState(false)
	const [, setIsSubmitting] = useState(false)

	const [publishStatus, publishTemplate, resetPublishStatus] = useAsyncResultIdle(
		(templateId: string, version: number) => publishEmailTemplateVersion(accessToken, {templateId, version})
	)

	// Track previous publish status to avoid infinite loops
	const publishStatusRef = useRef(publishStatus)

	useEffect(() => {
		// Only refresh if we have a new successful result and it's different from the previous one
		const prevStatus = publishStatusRef.current
		const isPrevSuccess = prevStatus && "value" in prevStatus
		const isCurrentSuccess = publishStatus && "value" in publishStatus

		if (isCurrentSuccess && (!isPrevSuccess || prevStatus !== publishStatus)) {
			refresh()
			// Reset status after handling to prevent re-triggering
			resetPublishStatus()
		}

		// Update the ref with current status
		publishStatusRef.current = publishStatus
	}, [publishStatus, refresh, resetPublishStatus])

	const viewTemplateVersions = (template: EmailTemplate) => {
		navigate(`/admin/email-templates/${template.attributes.templateId}`)
	}

	const startEditing = (template: EmailTemplate) => {
		setIsCreating(false)
		setEditingTemplateId(template.id)
		setEditingTemplate(template)
		setForcePublish(false)
		setError(null)
	}

	const startCreating = () => {
		setEditingTemplateId(null)
		setEditingTemplate(null)
		setNewTemplateId("")
		setNewTemplateSubject("")
		setIsCreating(true)
		setForcePublish(false)
	}

	const cancelOperation = () => {
		setEditingTemplateId(null)
		setEditingTemplate(null)
		setIsCreating(false)
		setNewTemplateId("")
		setNewTemplateSubject("")
		setError(null)
		setForcePublish(false)
	}

	const handleSaveTemplate = async (htmlContent: string, subject?: string, samples?: string) => {
		if (!editingTemplate) return

		setIsSubmitting(true)
		setError(null)

		const result = await createEmailTemplateVersion(accessToken, {
			subject: subject || editingTemplate.attributes.subject,
			htmlContent,
			templateId: editingTemplate.attributes.templateId,
			forcePublish: forcePublish,
			tags: {...(editingTemplate.attributes.tags || {}), domain: filteredDomains.join("")},
			samples: samples || "[]",
		})

		if (result.isErr()) {
			const apiError = result.error
			const message = "errors" in apiError ? apiError.errors[0]?.detail || apiError.errors[0]?.title : String(apiError)
			setError("Failed to update template: " + message)
			setIsSubmitting(false)
			return
		}

		setEditingTemplateId(null)
		setEditingTemplate(null)
		setForcePublish(false)
		setIsSubmitting(false)
		refresh()
	}

	const handleCreateTemplate = async (htmlContent: string, subject?: string, samples?: string) => {
		if (!newTemplateId) {
			setError("Template ID is required")
			return
		}

		if (!subject) {
			setError("Email subject is required")
			return
		}

		if (!filteredDomains.length || !filteredDomains.join("")) {
			setError("Domain is required")
			return
		}

		setIsSubmitting(true)
		setError(null)

		const result = await createEmailTemplate(accessToken, {
			subject: subject,
			htmlContent,
			templateId: newTemplateId,
			samples: samples || "[]",
			tags: {domain: filteredDomains.join("")},
		})

		if (result.isErr()) {
			const apiError = result.error
			const message = "errors" in apiError ? apiError.errors[0]?.detail || apiError.errors[0]?.title : String(apiError)
			setError("Failed to create template: " + message)
			setIsSubmitting(false)
			return
		}

		setIsCreating(false)
		setNewTemplateId("")
		setNewTemplateSubject("")
		setIsSubmitting(false)
		refresh()
	}

	const [query, setQuery] = useQueryState("filter[query]", "")

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
	}

	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Template", 500, onSearch)

	useEffect(() => {
		setIsSearchLoading(false)
	}, [query, setIsSearchLoading])

	const [filteredDomains, setFilteredDomains] = useQueryState<string[]>("filter[domains]", [])

	//drop domain filter upon changing to editing or creating
	useEffect(() => {
		setFilteredDomains([])
	}, [editingTemplateId, isCreating])

	// Reset error when editingTemplate changes
	useEffect(() => {
		if (editingTemplate) {
			setError(null)
		}
	}, [editingTemplate])

	const renderComponent = () => {
		if (isCreating) {
			return (
				<CreateEmailTemplate
					newTemplateId={newTemplateId}
					setNewTemplateId={setNewTemplateId}
					error={error}
					handleCreateTemplate={handleCreateTemplate}
					newTemplateSubject={newTemplateSubject}
					filteredDomains={filteredDomains}
					setFilteredDomains={setFilteredDomains}
				/>
			)
		}

		if (editingTemplateId && editingTemplate) {
			return (
				<EditEmailTemplate
					editingTemplate={editingTemplate}
					forcePublish={forcePublish}
					setForcePublish={setForcePublish}
					error={error}
					handleSaveTemplate={handleSaveTemplate}
					filteredDomains={filteredDomains}
					setFilteredDomains={setFilteredDomains}
				/>
			)
		}

		return (
			<EmailTemplatesDataTable
				result={result}
				filteredDomains={filteredDomains}
				query={query}
				viewTemplateVersions={viewTemplateVersions}
				startEditing={startEditing}
				publishTemplate={publishTemplate}
			/>
		)
	}

	return (
		<div className={"email-templates columns"}>
			<DataTableCard className={"email-templates-card column is-12"}>
				<TitleBar title="Email Templates">
					{!editingTemplateId && !isCreating ? (
						<Button theme={"neutral"} prefixIcon={"add-plus-small"} onClick={startCreating} className={"ml-4"}>
							New
						</Button>
					) : (
						<Button theme={"neutral"} onClick={cancelOperation} className={"ml-4"}>
							Cancel
						</Button>
					)}
				</TitleBar>
				{!isCreating && !editingTemplateId && !editingTemplate && (
					<DataTableActionHeader
						searchBox={searchBox}
						filters={[
							<DomainFilter
								statuses={filteredDomains}
								setStatuses={setFilteredDomains}
								title="Domain"
								key="domain-filter"
							/>,
						]}
						className="email-templates-action-header"
						enableSticky={true}
					/>
				)}
				{renderComponent()}
			</DataTableCard>
		</div>
	)
}
